import React, { FC } from 'react';

import Api from '~/Api';
import { PRODUCTS_PROJECT_CONFIG } from '~/constants/products';
import {
  type ModalStateType,
  useModalContext,
} from '~/contexts/modal/ModalContext';
import useErrors from '~/hooks/useErrors';
import UpgradeSubscriptionCheckout, {
  singleScreenModalConfig,
} from '~/modules/settings/components/modals/UpgradeSubscriptionCheckout/variants/SingleScreen';
import analytics from '~/services/analytics';
import { SubscriptionUpgradePaywallProps } from '~/services/compliance/experience';
import { useActions } from '~/store/hooks/useActions';
import { timeout } from '~/utils/common';

export const useUpgradeSubscription = (options: {
  purchaseSource: string;
  Paywall?: FC<SubscriptionUpgradePaywallProps>;
  paywallModalConfig?: Partial<ModalStateType>;
}): {
  upgradeSubscription: () => Promise<void>;
} => {
  const { Paywall, paywallModalConfig, purchaseSource } = options;
  const { loadAndSetActualProfileData } = useActions();
  const { reportUserErrors } = useErrors();
  const { handleOpenModal } = useModalContext();

  const upgradeSubscription = async (): Promise<void> => {
    try {
      const product = await Api.getProductById(
        PRODUCTS_PROJECT_CONFIG.ANNUAL_SUB_OFFER_ID,
      );

      analytics.trackEvent('subscription - change sub', {
        sub_id: product.id,
      });

      const SelectedPaywall = Paywall ?? UpgradeSubscriptionCheckout;
      const onSuccessPayment = async (): Promise<void> => {
        await timeout(4000);
        await loadAndSetActualProfileData();
      };

      handleOpenModal({
        onClose: () =>
          analytics.trackEvent('platform checkout - close', {
            purchase_source: purchaseSource,
          }),
        ...(paywallModalConfig ?? singleScreenModalConfig),
        component: ({ onClose }) => (
          <SelectedPaywall
            product={product}
            purchaseSource={purchaseSource}
            onSuccessPayment={async () => {
              await onSuccessPayment();
              onClose();
            }}
          />
        ),
      });
    } catch (e: any) {
      reportUserErrors({
        error: e,
        method: 'getProductById',
        userMessage:
          'Unable to fetch product details. Please retry or report issue',
      });
    }
  };

  return {
    upgradeSubscription,
  };
};
